import React from "react";
import { Link } from "react-router-dom";
import { Building2, Users, BadgeCheck, BarChart3, Megaphone, ChevronRight, CheckCircle2, ArrowRight } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import PublicHeader from "@/components/shared/PublicHeader";
import Footer from "@/components/Footer";

export default function ForProvidersPage() {
    const { lang } = useI18n();

    const reasons = [
        {
            Icon: Users,
            title: lang === "ar" ? "وصول لقاعدة متنامية من الأعضاء" : "Access a Growing Member Base",
            body: lang === "ar"
                ? "تواصل مع آلاف الأعضاء المهتمين بصحتهم الذين يبحثون بنشاط عن مقدمي الرعاية الصحية عبر منصة حماية."
                : "Connect with thousands of health-conscious members actively searching for healthcare providers through the Hemaia platform.",
        },
        {
            Icon: BadgeCheck,
            title: lang === "ar" ? "صفر عمولة — نموذج شراكة شفاف" : "Zero Commission — Transparent Partnership",
            body: lang === "ar"
                ? "لا نأخذ عمولة على استشاراتك. شراكتنا قائمة على الشفافية الكاملة ومصلحة مشتركة في نجاحك."
                : "We take no commission on your consultations. Our partnership is built on full transparency and a shared interest in your success.",
        },
        {
            Icon: BarChart3,
            title: lang === "ar" ? "رؤية فورية للمواعيد ودعم الحجز" : "Real-Time Appointment Visibility",
            body: lang === "ar"
                ? "احصل على رؤية فورية لتوافر مواعيدك ودعم في عمليات الحجز لضمان ملء جدولك باستمرار."
                : "Get real-time visibility into your appointment availability and booking support to keep your calendar consistently full.",
        },
        {
            Icon: Megaphone,
            title: lang === "ar" ? "حضور بارز عبر منصتنا وقنواتنا التسويقية" : "Featured Across Our Platform & Marketing",
            body: lang === "ar"
                ? "عيادتك أو مستشفاك يظهر بشكل بارز عبر منصة حماية وجميع قنواتنا التسويقية لضمان أقصى وصول."
                : "Your clinic or hospital is featured prominently across the Hemaia platform and all our marketing channels for maximum reach.",
        },
    ];

    const steps = [
        {
            num: "01",
            title: lang === "ar" ? "تواصل معنا" : "Reach Out",
            body: lang === "ar"
                ? "أرسل لنا طلب الانضمام عبر نموذج التواصل أو واتساب."
                : "Submit your join request via our contact form or WhatsApp.",
        },
        {
            num: "02",
            title: lang === "ar" ? "مراجعة ومؤهلات" : "Review & Onboarding",
            body: lang === "ar"
                ? "نراجع ملفك ونتحقق من الاعتمادات لضمان أعلى معايير الجودة لأعضائنا."
                : "We review your profile and verify credentials to ensure the highest quality standards for our members.",
        },
        {
            num: "03",
            title: lang === "ar" ? "إطلاق ملفك الشخصي" : "Go Live",
            body: lang === "ar"
                ? "يظهر ملفك الشخصي على المنصة ويبدأ أعضاء حماية في اكتشافك وحجز مواعيد."
                : "Your profile goes live and Hemaia members start discovering you and booking appointments.",
        },
        {
            num: "04",
            title: lang === "ar" ? "نمو مستمر" : "Grow Together",
            body: lang === "ar"
                ? "نتابع معك باستمرار ونوفر بيانات الأداء لمساعدتك على الاستفادة القصوى من شراكتنا."
                : "We check in regularly and provide performance data to help you get the most from our partnership.",
        },
    ];

    const specialties = lang === "ar" ? [
        "طب الأسنان", "طب العيون", "أمراض القلب", "العظام والمفاصل",
        "الأمراض الجلدية", "طب النساء والتوليد", "الأطفال", "الأنف والأذن والحنجرة",
        "الجراحة التجميلية", "الأورام", "الغدد الصماء", "طب الأعصاب",
    ] : [
        "Dentistry", "Ophthalmology", "Cardiology", "Orthopedics",
        "Dermatology", "Obstetrics & Gynecology", "Pediatrics", "ENT",
        "Aesthetic Surgery", "Oncology", "Endocrinology", "Neurology",
    ];

    return (
        <div className="min-h-screen bg-background" data-testid="for-providers-page">
            <PublicHeader />

            <main className="pt-16">
                {/* Hero */}
                <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-background py-20 sm:py-28">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                        <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs font-medium text-primary mb-6">
                            <Building2 className="h-3.5 w-3.5" />
                            {lang === "ar" ? "للمستشفيات والعيادات" : "For Hospitals & Clinics"}
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6">
                            {lang === "ar"
                                ? "املأ المزيد من المواعيد.\nاستقطب مرضى جدد.\nابنِ ولاءً دائماً."
                                : "Fill More Appointments.\nAttract New Patients.\nBuild Lasting Loyalty."}
                        </h1>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8">
                            {lang === "ar"
                                ? "انضم إلى شبكة مقدمي خدمات حماية ديسكفر — المنصة التي تربط الباحثين عن الرعاية الصحية في مصر بالعيادات المناسبة في الوقت المناسب."
                                : "Join Hemaia Discover's provider network — the platform connecting Egypt's healthcare seekers with the right clinics at the right time."}
                        </p>
                        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-5 py-2.5 text-sm font-semibold text-primary mb-8">
                            {lang === "ar" ? "نحن نتولى الاكتشاف — أنت تركّز على الرعاية" : "We handle the discovery — You focus on care"}
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-4">
                            <Link
                                to="/provider-signup"
                                className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
                            >
                                {lang === "ar" ? "انضم كمقدم خدمة" : "Join as a Provider"}
                                <ChevronRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Why providers choose us */}
                <section className="py-16 sm:py-20">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-foreground mb-3">
                                {lang === "ar" ? "لماذا يختارنا مقدمو الخدمة" : "Why Providers Choose Us"}
                            </h2>
                            <p className="text-muted-foreground max-w-xl mx-auto">
                                {lang === "ar"
                                    ? "شراكة حقيقية قائمة على النمو المشترك — لا عمولات خفية، لا تعقيدات."
                                    : "A real partnership built on mutual growth — no hidden commissions, no complexity."}
                            </p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-6">
                            {reasons.map(({ Icon, title, body }) => (
                                <div key={title} className="surface-card p-6 flex gap-4">
                                    <div className="shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Icon className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-foreground mb-1">{title}</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Specialties */}
                <section className="py-16 sm:py-20 bg-muted/30">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
                        <h2 className="text-3xl font-bold text-foreground mb-3">
                            {lang === "ar" ? "التخصصات التي نخدمها" : "Specialties We Serve"}
                        </h2>
                        <p className="text-muted-foreground mb-10">
                            {lang === "ar"
                                ? "شبكتنا تغطي أوسع نطاق من التخصصات الطبية في مصر."
                                : "Our network covers the widest range of medical specialties in Egypt."}
                        </p>
                        <div className="flex flex-wrap justify-center gap-3">
                            {specialties.map((s) => (
                                <span
                                    key={s}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-1.5 text-sm font-medium text-foreground"
                                >
                                    <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                                    {s}
                                </span>
                            ))}
                        </div>
                    </div>
                </section>

                {/* How It Works */}
                <section className="py-16 sm:py-20">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6">
                        <h2 className="text-3xl font-bold text-foreground text-center mb-12">
                            {lang === "ar" ? "كيف تنضم إلى الشبكة" : "How to Join the Network"}
                        </h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {steps.map(({ num, title, body }, i) => (
                                <div key={num} className="relative text-center">
                                    <div className="text-4xl font-bold text-primary/20 mb-3">{num}</div>
                                    <h3 className="font-semibold text-foreground mb-2">{title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
                                    {i < steps.length - 1 && (
                                        <ArrowRight className="hidden lg:block absolute top-8 -end-3 h-4 w-4 text-primary/30" />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-16 sm:py-20 bg-primary/5">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                        <Building2 className="h-10 w-10 text-primary mx-auto mb-4" />
                        <h2 className="text-3xl font-bold text-foreground mb-3">
                            {lang === "ar" ? "انضم إلى شبكة حماية اليوم" : "Join the Hemaia Network Today"}
                        </h2>
                        <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                            {lang === "ar"
                                ? "فريقنا جاهز للإجابة على أسئلتك وإرشادك خلال عملية الانضمام خطوة بخطوة."
                                : "Our team is ready to answer your questions and walk you through the onboarding process step by step."}
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Link
                                to="/provider-signup"
                                className="inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
                            >
                                {lang === "ar" ? "تقدّم بطلب الانضمام" : "Apply to Join"}
                                <ChevronRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
